import * as React from 'react';
import { Alert, Table } from 'reactstrap';
import { TranslationFunction } from 'i18next';
import { formatAdminDateTime } from '../../utils/adminDateTime';
import { ScheduledCommunicationsStatus } from './scheduledTypes';

interface Props {
  status: ScheduledCommunicationsStatus | null;
  loading: boolean;
  error: string;
  t: TranslationFunction;
  timeZone?: string;
  /** When true, show full lifecycle tables; otherwise a compact summary. */
  detailed?: boolean;
}

const formatWhen = (iso: string | undefined, timeZone?: string) =>
  formatAdminDateTime(iso, timeZone);

/**
 * Plain-language scheduled communications lifecycle.
 * "Needs Review" is only shown when the API includes a warning that says so
 * (e.g. marketing/copy mismatch without launchWelcomeAt config) — not inferred locally.
 */
const ScheduledStatusPanel: React.FC<Props> = ({
  status, loading, error, t, timeZone, detailed = true
}) => {
  if (loading) {
    return <p className="cc__lighttext">{t('Loading…')}</p>;
  }
  if (error) {
    return <Alert color="warning">{error}</Alert>;
  }
  if (!status) {
    return <p className="cc__lighttext">{t('Scheduled status is not available yet.')}</p>;
  }

  const tz = timeZone || status.timezone;
  const start = status.competitionStart;
  const welcomeAt = status.launchWelcomeAt || (status.launchWelcome && status.launchWelcome.scheduledFor);
  const welcomeDiffers = Boolean(
    start && welcomeAt && new Date(start).getTime() !== new Date(welcomeAt).getTime()
  );
  const warnings = status.warnings || [];
  const needsReviewWarnings = warnings.filter((w) =>
    /needs review/i.test(w)
  );
  const otherWarnings = warnings.filter((w) => !/needs review/i.test(w));
  const welcome = status.launchWelcome;
  const signup = status.signupConfirmation;
  const check = status.lastCommsCheck;

  return (
    <div>
      {needsReviewWarnings.map((w) =>
        <Alert key={w} color="warning">
          <strong>{t('Needs Review')}:</strong> {w}
        </Alert>
      )}
      {otherWarnings.map((w) =>
        <Alert key={w} color="warning">{w}</Alert>
      )}

      <dl style={{ marginBottom: 16 }}>
        <dt>{t('Challenge start')}</dt>
        <dd>{formatWhen(start, tz)}
          {tz ? ` (${tz})` : ''}
        </dd>
        {welcomeDiffers &&
          <>
            <dt>{t('Launch welcome email')}</dt>
            <dd>{formatWhen(welcomeAt, tz)}</dd>
          </>
        }
        {!welcomeDiffers && welcomeAt &&
          <>
            <dt>{t('Launch welcome email')}</dt>
            <dd>{formatWhen(welcomeAt, tz)} — {t('same as Challenge start')}</dd>
          </>
        }
      </dl>

      {detailed &&
        <Table responsive={true} size="sm" bordered={true}>
          <thead>
            <tr>
              <th>{t('Message')}</th>
              <th>{t('When')}</th>
              <th>{t('Template')}</th>
              <th>{t('Status')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('Signup confirmation')}</td>
              <td>{t('Immediately on preregistration')}</td>
              <td>
                {(signup && signup.templateName) || (signup && signup.templateKey) || '—'}
                {signup && signup.templateEnabled === false ? ` (${t('disabled')})` : ''}
              </td>
              <td>{t('Transactional — sent when someone signs up')}</td>
            </tr>
            <tr>
              <td>{t('Launch welcome')}</td>
              <td>{formatWhen(welcomeAt || (welcome && welcome.scheduledFor), tz)}</td>
              <td>
                {(welcome && welcome.templateName) || (welcome && welcome.templateKey) || '—'}
                {welcome && welcome.templateEnabled === false ? ` (${t('disabled')})` : ''}
              </td>
              <td>
                {welcome
                  ? `${welcome.successfullySent || 0} ${t('sent')}, ${welcome.pending || 0} ${t('pending')} (${welcome.preregistered || 0} ${t('preregistered')})`
                  : '—'}
              </td>
            </tr>
          </tbody>
        </Table>
      }

      {check &&
        <p className="cc__lighttext" style={{ marginTop: 12, marginBottom: 0 }}>
          {t('Last automated communications check')}:{' '}
          {formatWhen(check.completedAt || check.startedAt, tz)}
          {check.result ? ` (${check.result})` : ''}
          {check.stale &&
            <span style={{ color: '#856404', marginLeft: 8 }}>
              — {t('Warning: this check looks stale (scheduler may not be running).')}
            </span>
          }
        </p>
      }
    </div>
  );
};

export default ScheduledStatusPanel;
